import type { RangeValue } from '@heroui/react';
import {
  DateFormatter,
  type DateValue,
  now,
  parseDate,
  startOfMonth,
  startOfWeek,
  startOfYear,
  type ZonedDateTime,
} from '@internationalized/date';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EDateFilter, LOCALE } from '@/config';

dayjs.extend(utc);

export const toUnix = (date: string | Date) => {
  const unixTimestamp = Math.floor(new Date(date).getTime() / 1000);
  return unixTimestamp;
};

/** Parses a date string (YYYY-MM-DD or ISO) to DateValue for use with DatePicker. */
export const parseDateValue = (dateStr: string | null): DateValue | null => {
  if (!dateStr || dateStr.length < 10) return null;
  try {
    return parseDate(dateStr.slice(0, 10));
  } catch {
    return null;
  }
};

export const formatDate = (date: Date | string | number, format = 'DD/MM/YYYY HH:mm:ss') => {
  return dayjs(new Date(date)).format(format);
};

export const formatDateFromUnix = (unixTimestamp: number, format = 'DD MMM YY | HH:mm:ss') => {
  return dayjs.unix(unixTimestamp).format(format);
};

export function getStartAndEndOfDayUnix(dates: RangeValue<DateValue>) {
  const startOfDay = dayjs(dates.start.toDate('utc')).utc(false).startOf('day').unix();
  const endOfDay = dayjs(dates.end.toDate('utc')).utc(false).endOf('day').unix();

  return {
    from: startOfDay,
    to: endOfDay,
  };
}

export const formatUnix = (unixTimestamp: number) => {
  const date = new Date(unixTimestamp * 1000);
  const formatter = new DateFormatter(LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const formattedDate = formatter.format(date);
  return formattedDate;
};

export const getDateRange = (filter: EDateFilter) => {
  const today = now('utc');
  let from: ZonedDateTime | null;
  let to: ZonedDateTime | null;
  const locale = LOCALE;

  switch (filter) {
    case EDateFilter.ALL: {
      return { from: null, to: null, start: null, end: null };
    }
    case EDateFilter.TODAY: {
      from = today.add({ days: 0 });
      to = from.add({ days: 0 });
      break;
    }
    case EDateFilter.YESTERDAY: {
      from = today.subtract({ days: 1 });
      to = from.add({ days: 0 });
      break;
    }
    case EDateFilter.THIS_WEEK: {
      from = startOfWeek(today, locale);
      to = from.add({ days: 6 });
      break;
    }
    case EDateFilter.LAST_WEEK: {
      to = startOfWeek(today, locale).subtract({ days: 1 });
      from = to.subtract({ days: 6 });
      break;
    }
    case EDateFilter.PAST_TWO_WEEKS: {
      to = startOfWeek(today, locale).subtract({ days: 1 });
      from = to.subtract({ days: 13 });
      break;
    }
    case EDateFilter.THIS_MONTH: {
      from = startOfMonth(today);
      to = from.add({ months: 1 }).subtract({ days: 1 });
      break;
    }
    case EDateFilter.LAST_MONTH: {
      const firstDayThisMonth = startOfMonth(today);
      to = firstDayThisMonth.subtract({ days: 1 });
      from = startOfMonth(to);
      break;
    }
    case EDateFilter.LAST_THREE_MONTHS: {
      const firstDayThisMonth = startOfMonth(today);
      to = firstDayThisMonth.subtract({ days: 1 });
      from = startOfMonth(to).subtract({ months: 2 });
      break;
    }
    case EDateFilter.LAST_SIX_MONTHS: {
      const firstDayThisMonth = startOfMonth(today);
      to = firstDayThisMonth.subtract({ days: 1 });
      from = startOfMonth(to).subtract({ months: 5 });
      break;
    }
    case EDateFilter.LAST_TWELVE_MONTHS: {
      const firstDayThisMonth = startOfMonth(today);
      to = firstDayThisMonth.subtract({ days: 1 });
      from = startOfMonth(to).subtract({ months: 11 });
      break;
    }
    case EDateFilter.THIS_YEAR: {
      from = startOfYear(today);
      to = from.add({ years: 1 }).subtract({ days: 1 });
      break;
    }
    case EDateFilter.LAST_YEAR: {
      const firstDayThisYear = startOfYear(today);
      to = firstDayThisYear.subtract({ days: 1 });
      from = startOfYear(to);
      break;
    }

    default:
      throw new Error('Invalid range type');
  }

  return {
    ...getStartAndEndOfDayUnix({ end: to, start: from }),
    start: from.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
    end: to.set({ hour: 23, minute: 59, second: 59, millisecond: 999 }),
  };
};

export function formatCurrency(amount: number, currency = 'GBP', locale = LOCALE) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

export function isToday(date: Date | string | number) {
  return dayjs(date).isSame(dayjs(), 'day');
}

export function isYesterday(date: Date | string | number) {
  return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
}

export function formatDateDivider(date: Date | string | number) {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return dayjs(date).format('D MMM YYYY');
}
